import React from "react";
import styled from "styled-components";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { DataEditControl } from "../DataEditControl";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../../rule-engine-core/Common/AppGlueAny";

export const GridTestTemplate = (args: { definition: DataDefinition }) => {
    let data = args.definition.getValueObject();
    const [selected, setSelected] = React.useState(0);

    return (
        <GridWrapper>
            {args.definition.fields.map((f, idx) => {
                let value = DataUtilities.get(data, f.name);
                if (!value && f instanceof ObjectDataDefinition) {
                    value = f.list ? [] : {};
                    DataUtilities.set(data, f.name, value, false);
                }

                return (
                    <Cell selected={selected === idx}>
                        <DataEditControl
                            value={value}
                            key={idx}
                            forData={f}
                            onUpdate={(newValue: AppGlueAny) => {
                                DataUtilities.set(args.definition.getValueObject(), f.name, undefined, false);
                                DataUtilities.set(args.definition.getValueObject(), f.name, newValue, false);
                            }}
                            mode={selected === idx ? "edit_mode" : "display_mode"}
                            navigate={(direction) => {
                                let newIdx = selected;
                                if (direction === "Left" && selected > 0) {
                                    newIdx -= 1;
                                } else if (
                                    (direction === "Right" || direction === "Tab") &&
                                    selected < args.definition.fields.length - 1
                                ) {
                                    newIdx += 1;
                                } else if (direction === "Select") {
                                    newIdx = idx;
                                } else if (direction === "Down" && selected + 5 <= args.definition.fields.length - 1) {
                                    newIdx += 5;
                                } else if (direction === "Up" && selected - 5 >= 0) {
                                    newIdx -= 5;
                                }
                                setSelected(newIdx);
                            }}
                        />
                    </Cell>
                );
            })}
        </GridWrapper>
    );
};

const GridWrapper = styled.div`
    display: grid;
    justify-items: stretch;
    grid-template-columns: repeat(5, [col-start] max-content [col-end]);
    column-gap: 5px;
    row-gap: 5px;
`;

export const DisplayModeTemplate = (args: { definition: DataDefinition }) => {
    if (args.definition.fields.length === 0) return <></>;

    let forData = args.definition.fields[0];
    const value = DataUtilities.get(args.definition.getValueObject(), forData.name);

    return (
        <Cell>
            <DataEditControl
                value={value}
                forData={forData}
                onUpdate={(newValue: AppGlueAny) => {
                    DataUtilities.set(args.definition.getValueObject(), forData.name, undefined, false);
                    DataUtilities.set(args.definition.getValueObject(), forData.name, newValue, false);
                }}
                mode={"display_mode"}
            />
        </Cell>
    );
};

export const DisplayEditModeTemplate = (args: { definition: DataDefinition }) => {
    if (args.definition.fields.length === 0) return <></>;

    let forData = args.definition.fields[0];
    const value = DataUtilities.get(args.definition.getValueObject(), forData.name);

    return (
        <Cell>
            <DataEditControl
                value={value}
                forData={forData}
                onUpdate={(newValue: AppGlueAny) => {
                    DataUtilities.set(args.definition.getValueObject(), forData.name, undefined, false);
                    DataUtilities.set(args.definition.getValueObject(), forData.name, newValue, false);
                }}
                mode={"display_mode_edit"}
            />
        </Cell>
    );
};

export const DisplayLayerEditModeTemplate = (args: { definition: DataDefinition }) => {
    if (args.definition.fields.length === 0) return <></>;

    let forData = args.definition.fields[0];
    const value = DataUtilities.get(args.definition.getValueObject(), forData.name);

    return (
        <Cell>
            <DataEditControl
                value={value}
                forData={forData}
                onUpdate={(newValue: AppGlueAny) => {
                    DataUtilities.set(args.definition.getValueObject(), forData.name, undefined, false);
                    DataUtilities.set(args.definition.getValueObject(), forData.name, newValue, false);
                }}
                mode={"display_mode_edit"}
                editMode="layer"
            />
        </Cell>
    );
};

export const EditModeTemplate = (args: { definition: DataDefinition }) => {
    if (args.definition.fields.length === 0) return <></>;

    let forData = args.definition.fields[0];
    const value = DataUtilities.get(args.definition.getValueObject(), forData.name);

    return (
        <Cell>
            <DataEditControl
                value={value}
                forData={forData}
                onUpdate={(newValue: AppGlueAny) => {
                    DataUtilities.set(args.definition.getValueObject(), forData.name, undefined, false);
                    DataUtilities.set(args.definition.getValueObject(), forData.name, newValue, false);
                }}
                mode={"edit_mode"}
            />
        </Cell>
    );
};

const Cell = styled.div<{ selected?: boolean }>`
    display: inline-flex;
    ${(props) =>
        props.selected
            ? `border: 1px solid  ${UIStyleConstants.BLUE_COLOR};`
            : `border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};`}
`;
